import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { SpinnerGap, Plus, X, Trash } from '@phosphor-icons/react';
import { PersonLink } from '@/components/PersonLink';
import ScopeToggle from '@/components/ScopeToggle';
import useScopeFilter from '@/hooks/useScopeFilter';

function DealModal({ deal, types, statuses, onClose, onSave }) {
  const [form, setForm] = useState(deal ? { ...deal } : { ID_TipoNegocio: '', Referencia: '', Descricao: '', DataPrevistaFecho: '', DataInicio: '', ValorPrevisto: 0, Observacao: '' });
  const [saving, setSaving] = useState(false);
  const fmt = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v || 0);
  const handleSubmit = async (e) => { e.preventDefault(); setSaving(true); await onSave(form, !!deal); setSaving(false); };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-[550px] max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
          <h2 className="text-lg font-bold text-[#111827]">{deal ? 'Detalhes do Negócio' : 'Novo Negócio'}</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#F3F4F6]"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {!deal && types.length > 0 && (
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#6B7280] mb-1 block">Tipo de Negócio</label>
              <select value={form.ID_TipoNegocio} onChange={e => setForm({...form, ID_TipoNegocio: e.target.value})}
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829] outline-none">
                <option value="">Selecionar</option>
                {types.map(t => <option key={t.ID_TipoNegocio} value={t.ID_TipoNegocio}>{t.Descricao}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#6B7280] mb-1 block">Referência</label>
              <input data-testid="deal-ref" value={form.Referencia || ''} onChange={e => setForm({...form, Referencia: e.target.value})}
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829] outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#6B7280] mb-1 block">Valor Previsto</label>
              <input type="number" step="0.01" value={form.ValorPrevisto || 0} onChange={e => setForm({...form, ValorPrevisto: parseFloat(e.target.value) || 0})}
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm font-mono focus:ring-2 focus:ring-[#0F1829] outline-none" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#6B7280] mb-1 block">Descrição</label>
            <input value={form.Descricao || ''} onChange={e => setForm({...form, Descricao: e.target.value})}
              className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829] outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#6B7280] mb-1 block">Data Início</label>
              <input type="date" value={form.DataInicio ? form.DataInicio.substring(0,10) : ''} onChange={e => setForm({...form, DataInicio: e.target.value})}
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829] outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#6B7280] mb-1 block">Data Prev. Fecho</label>
              <input type="date" value={form.DataPrevistaFecho ? form.DataPrevistaFecho.substring(0,10) : ''} onChange={e => setForm({...form, DataPrevistaFecho: e.target.value})}
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829] outline-none" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#6B7280] mb-1 block">Observação</label>
            <textarea value={form.Observacao || form.Observacoes || ''} onChange={e => setForm({...form, Observacao: e.target.value})} rows={2}
              className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829] outline-none resize-none" />
          </div>
          {deal && (
            <div className="bg-[#F9FAFB] rounded-md p-3 text-xs text-[#6B7280] space-y-1">
              <p>Estado: <span className="font-medium text-[#111827]">{deal.EstadoNegocio || deal.TipoNegocio || '-'}</span></p>
              <p>Data Registo: <span className="font-medium text-[#111827]">{deal.DataRegisto || deal.DataRegistoStr || '-'}</span></p>
              <p>Cliente: <span className="font-medium text-[#111827]">{deal.Cliente_NomeCompleto || '-'}</span></p>
            </div>
          )}
          <div className="flex items-center justify-between pt-3 border-t border-[#E5E7EB]">
            <div>{deal && <button type="button" onClick={() => { api.post('/negocios/remove', { ID_Negocio: deal.ID_Negocio }).then(() => { onClose(); window.location.reload(); }); }} className="text-sm text-[#FF2B2B] hover:underline flex items-center gap-1"><Trash size={14} /> Remover</button>}</div>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-md hover:bg-[#F3F4F6]">Fechar</button>
              <button type="submit" disabled={saving} data-testid="deal-save-btn"
                className="px-4 py-2 text-sm bg-[#0F1829] text-white rounded-md hover:bg-[#002480] flex items-center gap-2 disabled:opacity-50">
                {saving && <SpinnerGap size={14} className="animate-spin" />}{deal ? 'Guardar' : 'Criar'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function DealsPage() {
  const [deals, setDeals] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [filter, setFilter] = useState('');
  const [scope, setScope] = useScopeFilter('all');
  const navigate = useNavigate();
  const fmt = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v || 0);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.post('/negocios/list', { scope }).then(r => setDeals(r.data?.data || [])),
      api.post('/negocios/types').then(r => setTypes(Array.isArray(r.data) ? r.data : [])),
    ]).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [scope]);

  const handleSave = async (form, isEdit) => {
    if (isEdit) {
      await api.post('/negocios/edit', { ...form, ID_Negocio: form.ID_Negocio });
    } else {
      await api.post('/negocios/add', form);
    }
    setModal(null);
    load();
  };

  const filtered = deals.filter(d => !filter || String(d.ID_Negocio || '').includes(filter.trim()) || (d.Descricao || '').toLowerCase().includes(filter.toLowerCase()) || (d.Referencia || '').toLowerCase().includes(filter.toLowerCase()) || (d.Cliente_NomeCompleto || '').toLowerCase().includes(filter.toLowerCase()));

  if (loading) return <div className="flex justify-center py-20"><SpinnerGap size={28} className="animate-spin text-[#0F1829]" /></div>;

  return (
    <div data-testid="deals-page" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>Negócios</h1>
          <p className="text-sm text-[#6B7280] mt-1">{filtered.length} negócios registados</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ScopeToggle value={scope} onChange={setScope} testid="deals-scope-toggle" />
          <input placeholder="Filtrar por Nº, descrição, ref, cliente..." value={filter} onChange={e => setFilter(e.target.value)}
            className="px-3 py-2 border border-[#E5E7EB] rounded-md text-sm w-[280px] focus:ring-2 focus:ring-[#0F1829] outline-none" />
          <button onClick={() => setModal('new')} data-testid="new-deal-btn"
            className="px-4 py-2 bg-[#0F1829] text-white text-sm font-medium rounded-md hover:bg-[#002480] flex items-center gap-1.5">
            <Plus size={16} /> Novo Negócio
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full">
          <thead><tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
            <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Nº</th>
            <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Tipo</th>
            <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Ref.</th>
            <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Descrição</th>
            <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Cliente</th>
            <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Consultor</th>
            <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Estado</th>
            <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Data</th>
            <th className="text-right text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Valor</th>
          </tr></thead>
          <tbody>
            {filtered.slice(0, 50).map(d => (
              <tr key={d.ID_Negocio} onClick={() => navigate(`/negocios/${d.ID_Negocio}`)} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] cursor-pointer transition-colors" data-testid={`deal-row-${d.ID_Negocio}`}>
                <td className="px-4 py-3 text-sm font-mono text-[#0F1829] underline">{d.ID_Negocio}</td>
                <td className="px-4 py-3">
                  <span data-testid={`deal-tipo-${d.ID_Negocio}`} className="text-[9px] uppercase tracking-[0.15em] font-semibold px-1.5 py-0.5 rounded border bg-[#F3F4F6] text-[#0F1829] border-[#E5E7EB]">
                    {d.TipoNegocio || d.ID_TipoNegocio || '-'}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm font-mono text-[#6B7280]">{d.Referencia || '-'}</td>
                <td className="px-4 py-3 text-sm font-medium text-[#111827] max-w-[200px] truncate">{d.Descricao || '-'}</td>
                <td className="px-4 py-3 text-sm"><PersonLink id={d.ID_Cliente} name={d.Cliente_NomeCompleto} /></td>
                <td className="px-4 py-3 text-sm">
                  <PersonLink id={d.ID_Consultor} name={d.Consultor_NomeCompleto} />
                  {d.IsOwn === 0 && (
                    <span data-testid={`deal-via-badge-${d.ID_Negocio}`}
                      className="ml-2 inline-flex items-center text-[9px] uppercase tracking-[0.15em] font-semibold px-1.5 py-0.5 rounded border bg-amber-50 text-amber-800 border-amber-200">
                      via rede
                    </span>
                  )}
                </td>
                <td className="px-4 py-3"><span className="text-[9px] uppercase tracking-[0.15em] font-semibold px-1.5 py-0.5 rounded border bg-blue-50 text-[#0F1829] border-[#0F1829]/20">{d.EstadoNegocio || '-'}</span></td>
                <td className="px-4 py-3 text-sm text-[#6B7280]">{d.DataRegisto || '-'}</td>
                <td className="px-4 py-3 text-sm font-mono text-right text-[#111827]">{fmt(d.ValorPrevisto)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
      {filtered.length > 50 && <p className="text-center text-sm text-[#6B7280] mt-3">A mostrar 50 de {filtered.length} resultados</p>}

      {modal && <DealModal deal={modal === 'new' ? null : modal} types={types} statuses={[]} onClose={() => setModal(null)} onSave={handleSave} />}
    </div>
  );
}
